import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { billingManager, SubscriptionSummary } from '../business/billing/BillingManager';
import {
  FEATURE_CATALOG,
  FeatureKey,
  PlanTier,
  getFeatureDefinition,
  normalizeTier,
  tierIncludes,
} from '../business/billing/featureCatalog';
import { useAuth } from './AuthContext';
import { useAppConfig } from './AppConfigContext';
import { useAdmin } from './AdminContext';

/**
 * Plan/subscription state shared across the authenticated app.
 *
 * The actual fetch is deduplicated and cached in `billingManager.getSubscriptionSummary()`
 * (60s TTL + in-flight promise reuse), so multiple `useFeatureAccess` calls do NOT cause
 * duplicate network traffic — they all read the same cached summary.
 */
export interface PlanState {
  /** Normalized current tier, or null if no subscription / still loading. */
  tier: PlanTier | null;
  /** Raw subscription DTO with limits (`maxEventTypes`, `maxUsers`, `hasWhiteLabel`, …). */
  summary: SubscriptionSummary | null;
  /** True while the first fetch is in flight. */
  loading: boolean;
  /** True if any error prevented us from determining the plan. */
  errored: boolean;
  /** True iff the tenant has an active (or trial) subscription. */
  isActive: boolean;
  /** True iff the tenant has no subscription record at all. */
  noSubscription: boolean;
  /** True iff the subscription is managed by a partner (tenant cannot self-manage billing). */
  isPartnerManaged: boolean;
  /** Lightweight predicate — call from rendering code. */
  hasFeature: (feature: FeatureKey | string) => boolean;
  /** Look up the required tier (and metadata) for a feature key. */
  getFeatureRequirement: (feature: FeatureKey | string) => { requiredTier: PlanTier; label: string; description: string };
  /** Force a refetch — call after a successful plan change. */
  refresh: () => Promise<void>;
}

const DEFAULT_STATE: PlanState = {
  tier: null,
  summary: null,
  loading: true,
  errored: false,
  isActive: false,
  noSubscription: false,
  isPartnerManaged: false,
  hasFeature: () => true,
  getFeatureRequirement: (f) => {
    const def = getFeatureDefinition(f);
    return { requiredTier: def.requiredTier, label: def.label, description: def.description };
  },
  refresh: async () => {},
};

const PlanContext = createContext<PlanState>(DEFAULT_STATE);

export const usePlan = (): PlanState => useContext(PlanContext);

interface PlanProviderProps {
  children: React.ReactNode;
}

/**
 * Mounts inside the authenticated tenant routes. Loads the subscription once auth + app
 * config are settled, then exposes the plan + helpers via context.
 *
 * Host admins (no tenant context) and unauthenticated users get a permissive default —
 * we do NOT show upgrade prompts for those flows.
 */
export const PlanProvider: React.FC<PlanProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { config, isLoading: configLoading } = useAppConfig();
  const { isHostAdmin, isLoading: adminLoading } = useAdmin();

  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errored, setErrored] = useState<boolean>(false);

  const tenantId = config?.currentUser?.tenantId ?? null;
  const shouldSkipPlanFetch = !isAuthenticated || isHostAdmin || !tenantId;

  const fetchPlan = useCallback(async () => {
    if (authLoading || configLoading || adminLoading) return;
    if (shouldSkipPlanFetch) {
      setSummary(null);
      setErrored(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrored(false);
    try {
      const next = await billingManager.getSubscriptionSummary();
      setSummary(next);
    } catch {
      setSummary(null);
      setErrored(true);
    } finally {
      setLoading(false);
    }
  }, [authLoading, configLoading, adminLoading, shouldSkipPlanFetch]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  // Listen for cache invalidations triggered anywhere (e.g. BookingFormsPage refreshing
  // after a successful upgrade flow that goes through BookingFormLimitModal). Without
  // this listener the global plan tier would stay stale until next mount.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => { fetchPlan(); };
    window.addEventListener('routemize:plan-invalidated', handler);
    return () => window.removeEventListener('routemize:plan-invalidated', handler);
  }, [fetchPlan]);

  const refresh = useCallback(async () => {
    billingManager.invalidateSubscriptionCache();
    await fetchPlan();
  }, [fetchPlan]);

  const tier = useMemo<PlanTier | null>(() => normalizeTier(summary?.subscription?.currentTier ?? null), [summary]);

  const hasFeature = useCallback(
    (feature: FeatureKey | string): boolean => {
      // Permissive when we don't have plan info yet or are in a non-tenant context —
      // we never want to false-lock a button while loading.
      if (loading || errored || shouldSkipPlanFetch) return true;
      if (!tier) return true;
      const def = getFeatureDefinition(feature);
      // Cross-check known capability flags from the DTO when available, so a tenant
      // with manually-granted capabilities still passes.
      if (matchesCapabilityFlag(feature, summary)) return true;
      return tierIncludes(tier, def.requiredTier);
    },
    [loading, errored, shouldSkipPlanFetch, tier, summary]
  );

  const getFeatureRequirement = useCallback((feature: FeatureKey | string) => {
    const def = getFeatureDefinition(feature);
    return { requiredTier: def.requiredTier, label: def.label, description: def.description };
  }, []);

  const value = useMemo<PlanState>(
    () => ({
      tier,
      summary,
      loading,
      errored,
      isActive: summary?.isActive ?? false,
      noSubscription: summary?.noSubscriptionExists ?? false,
      isPartnerManaged: summary?.subscription?.isPartnerManaged ?? false,
      hasFeature,
      getFeatureRequirement,
      refresh,
    }),
    [tier, summary, loading, errored, hasFeature, getFeatureRequirement, refresh]
  );

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
};

// Hidden export for the rare callsite that needs to subscribe to the raw state
// without the helper shape — primarily for tests.
export { PlanContext };

/**
 * Some features have an explicit boolean on the subscription DTO (e.g. hasWhiteLabel).
 * When that flag is true we honor it even if the tier alone wouldn't include the feature
 * — this allows the backend to grandfather/grant features per-tenant.
 */
function matchesCapabilityFlag(feature: string, summary: SubscriptionSummary | null): boolean {
  const sub = summary?.subscription;
  if (!sub) return false;
  switch (feature) {
    case 'WhiteLabel': return !!sub.hasWhiteLabel;
    case 'CrmIntegrations': return !!sub.hasCrmIntegrations;
    case 'SameDayBookings': return !!sub.hasSameDayBookings;
    default: return false;
  }
}

// Re-export the catalog for convenience.
export { FEATURE_CATALOG };
export type { FeatureKey, PlanTier };
